import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { calculateFinance } from "../api/financeApi";

/* DealAlert — embedded Financing Calculator (SHIP B.6).
 *
 * Rendered inside the Listing Detail Page. Two flavours:
 *   - vertical="immo"  →  3 sliders (down_payment, interest, term)
 *                         + AT default Nebenkosten, KIM-V banner
 *   - vertical="auto"  →  4 sliders (down_payment, interest, term,
 *                                    residual_value)
 *
 * Backend contract: POST /api/finance/calculate
 *   - `interest_rate` is a DECIMAL (0.035 = 3.5%). UI shows percent,
 *     sends decimal.
 * Live update: debounced 200 ms on slider change.
 */

const DE = new Intl.NumberFormat("de-AT", { maximumFractionDigits: 0 });
const PCT = new Intl.NumberFormat("de-AT", {
  style: "percent", maximumFractionDigits: 2, minimumFractionDigits: 2,
});

function fmt(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return DE.format(Math.round(n)) + " €";
}

function fmtPct(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return PCT.format(n);
}

function Slider({
  label, value, onChange, min, max, step = 1, format,
  testid,
}) {
  return (
    <label className="block space-y-1" data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wider text-zinc-500">
          {label}
        </span>
        <span className="text-sm text-amber-300 tabular-nums">
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-amber-500"
        data-testid={`${testid}-range`}
      />
      <div className="flex justify-between text-[10px] text-zinc-600 tabular-nums">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </label>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <label className="block space-y-1" data-testid={testid}>
      <span className="text-xs uppercase tracking-wider text-zinc-500">
        {label}
      </span>
      <input
        type="number"
        min={0}
        step={50}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 tabular-nums"
        data-testid={`${testid}-input`}
      />
    </label>
  );
}

export default function FinanceCalculator({ card, onResult }) {
  const { t } = useTranslation(["mobile", "heatmap"]);
  const vertical = card?.vertical || "immo";
  // V4.22 — no hardcoded country list: the backend finance registry is
  // the single source of truth (unknown countries resolve to AT there).
  const country = (card?.country || "AT").toUpperCase();
  const cardPostal = card?.postal_code || card?.location?.postal_code || null;
  const listingPrice = Number(card?.price) || 0;

  // Initial defaults — vertical-specific.
  const initialDownPayment =
    vertical === "immo"
      ? Math.round(listingPrice * 0.25)        // 25 % equity → KIM-V-friendly start
      : Math.round(listingPrice * 0.20);
  const [downPayment, setDownPayment] = useState(initialDownPayment);
  const [interestPct, setInterestPct] = useState(vertical === "immo" ? 3.9 : 6.9);
  const [termMonths, setTermMonths] = useState(vertical === "immo" ? 300 : 60);
  const [residualValue, setResidualValue] = useState(
    vertical === "auto" ? Math.round(listingPrice * 0.25) : 0,
  );
  // Schuldendienstquote der FMA-Richtwerte braucht BEIDE Angaben. Leer
  // bleibt leer — daraus wird „nicht geprüft", niemals „erfüllt".
  const [netIncome, setNetIncome] = useState("");
  const [existingDebt, setExistingDebt] = useState("");

  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  // Re-run whenever any input changes — debounced 200 ms.
  useEffect(() => {
    if (!listingPrice || listingPrice <= 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError(null);
      const payload = {
        vertical,
        country,
        listing_ulid: card?.id || undefined,
        price: listingPrice,
        down_payment: downPayment,
        term_months: termMonths,
        interest_rate: Number((interestPct / 100).toFixed(4)),
      };
      // V4.22 — Bundesland wird automatisch aus der PLZ abgeleitet (immo only)
      if (vertical === "immo" && cardPostal) {
        payload.postal_code = cardPostal;
      }
      if (vertical === "auto" && residualValue > 0) {
        payload.residual_value = residualValue;
      }
      if (vertical === "immo") {
        const inc = Number(netIncome);
        if (netIncome !== "" && Number.isFinite(inc) && inc > 0) {
          payload.monthly_net_income = inc;
        }
        const debt = Number(existingDebt);
        if (existingDebt !== "" && Number.isFinite(debt) && debt >= 0) {
          payload.monthly_existing_debt = debt;
        }
      }
      try {
        const data = await calculateFinance(payload);
        setResult(data);
        if (typeof onResult === "function") onResult(data, payload);
      } catch (err) {
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            t("mobile:finance.calcFailed"),
        );
        setResult(null);
      } finally {
        setIsLoading(false);
      }
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downPayment, interestPct, termMonths, residualValue, vertical,
      listingPrice, netIncome, existingDebt]);

  const equityRatio = useMemo(
    () => (listingPrice > 0 ? downPayment / listingPrice : null),
    [downPayment, listingPrice],
  );

  if (!listingPrice) return null;

  return (
    <section
      data-testid="finance-calculator"
      className="space-y-4 rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5"
    >
      <header className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500">
            {t("mobile:finance.title")}
          </h2>
          <div className="text-sm text-zinc-200">
            {vertical === "immo" ? t("mobile:finance.immoLoan") : t("mobile:finance.autoLoan")}{" "}
            <span className="text-zinc-500">({t(`heatmap:regions.${country}`)})</span>{" "}
            <span className="text-zinc-400">
              {t("mobile:finance.purchasePrice")} {fmt(listingPrice)}
            </span>
          </div>
        </div>
        {isLoading && (
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-300">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("mobile:finance.calculating")}
          </span>
        )}
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <Slider
          label={t("mobile:finance.downPayment")}
          value={downPayment}
          onChange={setDownPayment}
          min={0}
          max={Math.max(0, listingPrice)}
          step={Math.max(1000, Math.round(listingPrice / 200))}
          format={fmt}
          testid="finance-slider-down-payment"
        />
        <Slider
          label={t("mobile:finance.interest")}
          value={interestPct}
          onChange={setInterestPct}
          min={0}
          max={vertical === "immo" ? 8.0 : 12.0}
          step={0.1}
          format={(n) => `${n.toFixed(1)} %`}
          testid="finance-slider-interest"
        />
        <Slider
          label={t("mobile:finance.termMonths")}
          value={termMonths}
          onChange={(n) => setTermMonths(Math.round(n))}
          min={vertical === "immo" ? 60 : 12}
          max={vertical === "immo" ? 420 : 84}
          step={vertical === "immo" ? 12 : 6}
          format={(n) => t("mobile:finance.termFormat", { months: n, years: Math.round(n / 12) })}
          testid="finance-slider-term"
        />
        {vertical === "auto" && (
          <Slider
            label={t("mobile:finance.residualBalloon")}
            value={residualValue}
            onChange={setResidualValue}
            min={0}
            max={Math.max(0, listingPrice)}
            step={Math.max(500, Math.round(listingPrice / 200))}
            format={fmt}
            testid="finance-slider-residual"
          />
        )}
        {vertical === "immo" && (
          <>
            <NumberField
              label={t("mobile:finance.netIncome")}
              value={netIncome}
              onChange={setNetIncome}
              testid="finance-input-net-income"
            />
            <NumberField
              label={t("mobile:finance.existingDebt")}
              value={existingDebt}
              onChange={setExistingDebt}
              testid="finance-input-existing-debt"
            />
          </>
        )}
      </div>

      {/* Output */}
      <div
        data-testid="finance-output"
        className="grid grid-cols-2 gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 sm:grid-cols-4"
      >
        <Stat
          label={t("mobile:finance.monthlyRate")}
          value={fmt(result?.monthly_rate)}
          accent
          testid="finance-output-monthly-rate"
        />
        <Stat
          label={t("mobile:finance.totalInterest")}
          value={fmt(result?.total_interest)}
          testid="finance-output-total-interest"
        />
        <Stat
          label={t("mobile:finance.totalCost")}
          value={fmt(result?.total_cost)}
          testid="finance-output-total-cost"
        />
        {vertical === "immo" ? (
          <Stat
            label={t("mobile:finance.equityRatio")}
            value={fmtPct(equityRatio)}
            testid="finance-output-equity-ratio"
          />
        ) : (
          <Stat
            label={t("mobile:finance.residual")}
            value={fmt(result?.residual_balloon)}
            testid="finance-output-residual"
          />
        )}
      </div>

      {/* Aufsichtliche Richtwerte (AT: FMA-WIK-RS) — immo only.
          Drei Zustände: eingehalten / überschritten / nicht vollständig
          geprüft. Fehlende Angaben sind NIE „erfüllt". */}
      {vertical === "immo" && result?.lending_guideline && (
        <div
          data-testid="finance-lending-guideline"
          data-status={result.lending_guideline.status}
          className={`space-y-1 rounded-xl border px-4 py-2 text-xs ${
            result.lending_guideline.status === "met"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
              : result.lending_guideline.status === "not_met"
                ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                : "border-zinc-700 bg-zinc-800/40 text-zinc-300"
          }`}
        >
          <div>
            {result.lending_guideline.status === "met"
              ? `✓ ${t("mobile:finance.guidelineMet")}`
              : result.lending_guideline.status === "not_met"
                ? `⚠ ${t("mobile:finance.guidelineNotMet")} ` +
                  result.lending_guideline.failed
                    .map((k) => t(`mobile:finance.guidelineFail.${k}`))
                    .join(" · ")
                : `ⓘ ${t("mobile:finance.guidelineIncomplete")} ` +
                  result.lending_guideline.unchecked
                    .map((k) => t(`mobile:finance.guidelineOpen.${k}`))
                    .join(" · ")}
          </div>
          {result.lending_guideline.small_loan_exempt && (
            <div className="text-zinc-400">
              {t("mobile:finance.guidelineSmallLoan")}
            </div>
          )}
          <div className="text-[11px] leading-snug text-zinc-500">
            {t("mobile:finance.guidelineNote")}
          </div>
        </div>
      )}

      {/* Nebenkosten — immo only */}
      {vertical === "immo" && result && (
        <div
          data-testid="finance-side-costs"
          className="text-xs text-zinc-400 flex flex-wrap items-baseline gap-x-3 gap-y-1"
        >
          <span className="text-zinc-500">
            {t(`mobile:finance.sideCosts.${country}`, {
              defaultValue: t("mobile:finance.sideCosts.generic"),
            })}
          </span>
          <span className="text-zinc-200 tabular-nums">
            {fmt(result.side_costs_total)}
          </span>
          <span className="text-zinc-500">·</span>
          <span>
            {t("mobile:finance.totalUpfront")}{" "}
            <span className="text-zinc-200 tabular-nums">
              {fmt(result.upfront_cash_needed)}
            </span>
          </span>
          {/* V4.22 — resolved Bundesland / conservative-default hint */}
          {result?.fees_meta?.state_name && (
            <span data-testid="finance-state-resolved" className="text-emerald-300">
              {t("mobile:finance.stateResolved")} {result.fees_meta.state_name}{" "}
              ({((result.fees_applied?.land_transfer_tax_pct || 0) * 100)
                .toFixed(1).replace(".", ",")} % GrESt)
            </span>
          )}
          {result?.fees_meta?.state_hint && (
            <span data-testid="finance-state-hint" className="text-amber-300">
              {result.fees_meta.state_hint}
            </span>
          )}
        </div>
      )}

      {error && (
        <div
          data-testid="finance-error"
          className="rounded-xl border border-rose-500/40 bg-rose-950/40 px-4 py-2 text-xs text-rose-200"
        >
          {error}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="space-y-0.5">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div
        className={`tabular-nums ${
          accent ? "text-xl text-amber-400 font-medium" : "text-sm text-zinc-100"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
